import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { Video, VideosService } from '@app/shared/services/api/videos.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { VideoUploadDialogComponent } from '@app/videos/dialog/video-upload-dialog/video-upload-dialog.component';
import { cssVariables } from '@env/css-vars';
import { environment } from '@env/environment';
import { map as _map, orderBy, remove } from 'lodash';
import { of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-videos',
  templateUrl: './videos.component.html',
  styleUrls: ['./videos.component.scss'],
})
export class VideosComponent implements OnInit {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  videos: Video[] = [];

  constructor(
    private videosService: VideosService,
    public overlayLoader: OverlayLoaderService,
    public azureBlob: AzureBlobStorageService,
    private matDialog: MatDialog,
    private overlayService: OverlayLoaderService,
    public authService: AuthService,
    private appInsightsServiceDirective: AppInsightsServiceDirective
  ) {}

  ngOnInit() {
    this.initializeVideos();
  }

  public initializeVideos() {
    this.videos = [];
    this.overlayLoader.isLoading = true;
    this.videosService
      .getAll()
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((videos: Video[]) => {
          if (!videos?.length) {
            this.overlayLoader.isLoading = false;
            return of();
          }
          const internalVideos: Video[] = videos.filter((video: Video) => !video.isExternal);

          _map(internalVideos, (video) => {
            video.videoBlob = video.videoUrl;
            this.videos.push(video);
            this.setMimeType(video);
            if (video.duration) {
              video.durationString = new Date(video.duration * 1000).toISOString().slice(11, 19);
            }
          });

          return _map(videos, (video) => {
            video.thumbnailBlob = video.thumbnail ? this.azureBlob.getBlobImageSafeUrl(`${environment.tenant}/${video.thumbnail}`) : null;
            if (video.isExternal) {
              this.videos.push(video);
            }
            return video;
          });
        })
      )
      .subscribe({
        next: (data: any) => {
          if (data.length <= 0) {
            this.overlayLoader.isLoading = false;
            return;
          }
          this.videos = orderBy(this.videos, ['updatedOn'], ['desc']);
          this.overlayLoader.isLoading = false;
        },
        error: () => (this.overlayLoader.isLoading = false),
      });
  }

  trackByFn(_: number, item: Video) {
    return item.id;
  }

  showFullScreen(id: string) {
    const elem = document.getElementById(id) as any;

    if (elem && elem.requestFullscreen) {
      elem.requestFullscreen().catch(() => {});
    } else if (elem && elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    } else if (elem && elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    }
  }

  edit(video: Video) {
    this.matDialog
      .open(VideoUploadDialogComponent, {
        data: {
          video: video,
        },
        width: '1200px',
        panelClass: ['-md', 'video-upload-dialog'],
      })
      .afterClosed()
      .subscribe((refresh) => {
        if (refresh) {
          this.initializeVideos();
        }
      });
  }

  delete(video: Video) {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          title: 'Löschen Bestätigen',
          descriptionText: `Sind Sie sicher, dass Sie das Video sowie die Beschreibung löschen möchten?`,
          confirmText: 'Ja',
          declineText: 'Nein',
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.overlayService.isLoading = true;
          this.videosService.delete(video.id).subscribe({
            next: () => {
              remove(this.videos, (v) => v.id === video.id);
              this.overlayService.isLoading = false;
              this.matDialog.open(NotificationDialogComponent, {
                data: {
                  descriptionText: 'Video-Tutorial gelöscht',
                  descriptionFaClass: 'fa-trash fa-2x',
                  iconDescriptionAlign: true,
                  alignment: 'start',
                  orientation: 'row',
                },
                panelClass: ['-sm'],
              });
            },
            error: () => {
              this.overlayService.isLoading = false;
              this.showError();
            },
          });
        }
      });
  }

  showError() {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Error',
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
      },
      position: { top: '40px' },
      panelClass: ['-md'],
    });
  }

  launch(video: Video) {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.videoPlay, {
      title: video.title,
      isExternal: true,
    });
    window.open(video.videoUrl, '_blank');
  }

  setMimeType(video: Video) {
    if (video.videoMimeType) {
      video.modifiedMimeType = video.videoMimeType;
    }

    if (navigator.userAgent.toLowerCase().indexOf('trident') > -1) {
      video.modifiedMimeType += `;codecs="avc1.42E01E, mp4a.40.2"`;
    }
  }

  onPlay(video: Video) {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.videoPlay, {
      title: video.title,
      isExternal: false,
    });
  }
}
